import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  createFilteredUrl,
  isTileClickable, 
  getTileTooltip, 
  TileDataWithDrillDown 
} from '../../../utils/urlUtils';

interface LowStockProduct {
  productCode: string;
  productName: string;
  eshopStock: number;
  reserveStock: number;
  transportStock: number;
  averageDailySales: number;
  daysOfStockRemaining: number;
}

interface LowStockAlertTileProps {
  data: TileDataWithDrillDown & {
    status?: string;
    data?: {
      products?: LowStockProduct[];
      totalCount?: number;
    };
    error?: string;
    metadata?: {
      lastUpdated?: string;
      source?: string;
    };
  };
}

export const LowStockAlertTile: React.FC<LowStockAlertTileProps> = ({ data }) => {
  const navigate = useNavigate();
  
  const isClickable = isTileClickable(data);
  const tooltip = getTileTooltip(data);

  const handleClick = () => {
    if (isClickable && data.drillDown?.filters) {
      const url = createFilteredUrl('/logistics/inventory', data.drillDown.filters);
      navigate(url);
    }
  };

  const handleRowClick = (product: LowStockProduct) => {
    // Navigate to inventory with filter for specific product
    const url = createFilteredUrl('/logistics/inventory', { 
      search: product.productCode,
      sort: 'eshop_stock_asc' 
    });
    navigate(url);
  };

  // Error state
  if (data.status === 'error') {
    return (
      <div className="h-full flex items-center justify-center text-center">
        <div>
          <div className="text-red-500 dark:text-red-400 text-2xl mb-2">⚠️</div>
          <p className="text-red-600 dark:text-red-400 text-sm">{data.error || 'Chyba při načítání dat'}</p>
        </div>
      </div>
    );
  }

  const products = data.data?.products || [];
  const totalCount = data.data?.totalCount || 0;

  // No low stock products
  if (products.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-center">
        <div>
          <div className="text-green-500 dark:text-emerald-400 text-2xl mb-2">✅</div>
          <p className="text-green-600 dark:text-emerald-400 text-sm font-medium">Všechny produkty mají dostatečnou zásobu</p>
        </div>
      </div>
    );
  }

  // Format number with Czech locale
  const formatNumber = (num: number, decimals: number = 0): string => {
    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  return (
    <div className="h-full flex flex-col text-sm md:text-xs leading-relaxed">
      {/* Compact products list */}
      <div className="flex-1 overflow-auto">
        <div className="space-y-1">
          {products.slice(0, 2).map((product) => (
            <div
              key={product.productCode}
              className="p-3 md:p-2 min-h-12 hover:bg-gray-50 dark:hover:bg-white/5 active:bg-gray-100 dark:active:bg-white/10 cursor-pointer transition-colors duration-200 rounded border border-gray-100 dark:border-graphite-border"
              onClick={() => handleRowClick(product)}
              style={{ touchAction: 'manipulation' }}
            >
              {/* Two line product row */}
              <div>
                <div className="flex items-center justify-between text-base md:text-xs">
                  <div className="font-medium text-gray-900 dark:text-graphite-text truncate">
                    {product.productName}
                  </div>
                  <div className="font-mono text-sm md:text-xs text-gray-600 dark:text-graphite-muted">
                    {formatNumber(product.eshopStock)} / {formatNumber(product.reserveStock)} / {formatNumber(product.transportStock)}
                  </div>
                </div>
                <div className="text-sm md:text-xs text-gray-500 dark:text-graphite-muted">
                  {product.productCode}
                </div>
              </div>
            </div>
          ))}
          {totalCount > 2 && (
            <div className="text-center pt-1">
              <button
                onClick={handleClick}
                className="text-sm md:text-xs text-blue-600 dark:text-graphite-accent hover:text-blue-800 dark:hover:text-graphite-accent-strong hover:underline"
                title={tooltip}
              >
                +{totalCount - 2} dalších
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};